"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Grid,
  Divider,
  Chip,
  Alert,
} from "@mui/material"
import { Delete, Edit, Subscriptions, AccountBalance, Refresh } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import CustomerForm from "../../components/customers/CustomerForm"
import { getCustomer, updateCustomer, deleteCustomer, reactivateCustomer } from "../../services/customer.service"
import { getSubscription, getSubscriptionHistory } from "../../services/subscription.service"
import { getCreditsSummary } from "../../services/credit.service"
import StatusBadge from "../../components/common/StatusBadge"

const CustomerDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [subscriptionHistory, setSubscriptionHistory] = useState([])
  const [credits, setCredits] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    fetchCustomer()
  }, [id])

  const fetchCustomer = async () => {
    setLoading(true)
    try {
      const response = await getCustomer(id)
      setCustomer(response.data)

      // Fetch related data
      fetchSubscription(response.data._id)
      fetchCredits(response.data._id, response.data.organizationId)
    } catch (error) {
      showNotification("Failed to fetch customer details", "error")
      navigate("/customers")
    } finally {
      setLoading(false)
    }
  }

  const fetchSubscription = async (customerId) => {
    try {
      const subResponse = await getSubscription(customerId)
      // Make sure subscription and planId exist before setting state
      if (subResponse.data && subResponse.data.planId) {
        setSubscription(subResponse.data)
      } else {
        console.warn("Received subscription without planId:", subResponse.data)
        setSubscription(null)
      }

      const historyResponse = await getSubscriptionHistory(customerId)
      // Filter out history items without planId
      if (historyResponse.data && Array.isArray(historyResponse.data)) {
        const validHistory = historyResponse.data.filter((item) => item && item.planId)
        setSubscriptionHistory(validHistory)
      } else {
        setSubscriptionHistory([])
      }
    } catch (error) {
      console.error("Failed to fetch subscription:", error)
      setSubscription(null)
      setSubscriptionHistory([])
    }
  }

  const fetchCredits = async (customerId, organizationId) => {
    try {
      const response = await getCreditsSummary(customerId, organizationId)
      setCredits(response.data)
    } catch (error) {
      console.error("Failed to fetch credits:", error)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleEdit = () => {
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
  }

  const handleUpdate = async (formData) => {
    setSaving(true)
    try {
      const response = await updateCustomer(id, formData)
      setCustomer(response.data)
      showNotification("Customer updated successfully", "success")
      setEditMode(false)
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to update customer", "error")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteCustomer(id)
      showNotification("Customer deleted successfully", "success")
      navigate("/customers")
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to delete customer", "error")
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  const handleManageSubscription = () => {
    navigate(`/subscriptions/customer/${id}`)
  }

  const handleManageCredits = () => {
    navigate(`/credits/customer/${id}`)
  }

  const handleReactivate = async () => {
    try {
      setLoading(true)
      const response = await reactivateCustomer(id)
      showNotification("Customer reactivated successfully", "success")
      setCustomer(response.data)
      fetchCustomer() // Refresh data
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to reactivate customer", "error")
    } finally {
      setLoading(false)
    }
  }

  if (loading || !customer) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={customer.name}
        subtitle={customer.email || "No email provided"}
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Customers", link: "/customers" },
          { text: customer.name },
        ]}
        action
        actionText="Delete Customer"
        actionIcon={<Delete />}
        actionColor="error"
        onActionClick={() => setDeleteDialogOpen(true)}
      />

      {!editMode ? (
        <>
          <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
            <Button variant="outlined" startIcon={<Edit />} onClick={handleEdit}>
              Edit Customer
            </Button>

            {customer.status === "inactive" ? (
              <Button variant="outlined" color="success" startIcon={<Refresh />} onClick={handleReactivate}>
                Reactivate Customer
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Subscriptions />}
                  onClick={handleManageSubscription}
                  disabled={customer.status === "inactive"}
                >
                  Manage Subscription
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={handleManageCredits}
                  disabled={customer.status === "inactive"}
                >
                  Manage Credits
                </Button>
              </>
            )}
          </Box>

          {customer.status === "inactive" && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This customer is currently inactive. Subscriptions and credits cannot be managed until the customer is
              reactivated.
            </Alert>
          )}

          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="customer tabs"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Overview" />
              <Tab label="Subscription" />
              <Tab label="Credits" />
              <Tab label="Contact & Address" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Customer Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={customer.status}
                          color={
                            customer.status === "active" ? "success" : customer.status === "trial" ? "info" : "default"
                          }
                        />
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          External ID
                        </Typography>
                        <Typography variant="body1">{customer.externalId || "Not set"}</Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body1">{new Date(customer.createdAt).toLocaleDateString()}</Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Subscription Status
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {subscription ? (
                        <>
                          {subscription && subscription.planId && subscription.planId.name && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                              <Typography variant="body1">{subscription.planId.name} Plan</Typography>
                              <StatusBadge status={subscription.status} />
                            </Box>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Billing Cycle: {subscription.billingCycle}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Price: ${subscription.price} {subscription.currency}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Current Period: {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{" "}
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                          </Typography>
                          <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={handleManageSubscription}>
                            Manage Subscription
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            No active subscription
                          </Typography>
                          <Button variant="contained" size="small" onClick={handleManageSubscription}>
                            Create Subscription
                          </Button>
                        </>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Credit Balance
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {credits ? (
                        <>
                          <Typography variant="h3" gutterBottom>
                            {credits.availableCredits}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Available credits
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Total Credits: {credits.totalCredits}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Used Credits: {credits.totalUsage}
                          </Typography>
                          <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={handleManageCredits}>
                            Manage Credits
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            No credits information available
                          </Typography>
                          <Button variant="contained" size="small" onClick={handleManageCredits}>
                            Add Credits
                          </Button>
                        </>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Current Subscription
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {subscription ? (
                    <Paper sx={{ p: 3, mb: 3 }}>
                      {subscription && subscription.planId && (
                        <Typography variant="h6">{subscription.planId.name} Plan</Typography>
                      )}
                      <Divider sx={{ mb: 3 }} />

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Billing Cycle
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2, textTransform: "capitalize" }}>
                            {subscription.billingCycle}
                          </Typography>

                          <Typography variant="subtitle2" color="text.secondary">
                            Price
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            ${subscription.price} {subscription.currency}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Current Period
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{" "}
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                          </Typography>

                          {subscription.trialEndDate && (
                            <>
                              <Typography variant="subtitle2" color="text.secondary">
                                Trial Ends
                              </Typography>
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {new Date(subscription.trialEndDate).toLocaleDateString()}
                              </Typography>
                            </>
                          )}

                          <Typography variant="subtitle2" color="text.secondary">
                            Auto Renew
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {subscription.autoRenew ? "Yes" : "No"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 3 }}>
                        <Button variant="contained" onClick={handleManageSubscription}>
                          Manage Subscription
                        </Button>
                      </Box>
                    </Paper>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        This customer doesn't have an active subscription.
                      </Typography>
                      <Button variant="contained" onClick={handleManageSubscription}>
                        Create Subscription
                      </Button>
                    </Box>
                  )}

                  {subscriptionHistory && subscriptionHistory.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        Subscription History
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                        {subscriptionHistory
                          .filter((item) => item && item.planId)
                          .map((item, index) => (
                            <Box
                              key={item._id}
                              sx={{
                                mb: 2,
                                pb: 2,
                                borderBottom: index < subscriptionHistory.length - 1 ? "1px solid #eee" : "none",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
                              >
                                <Typography variant="subtitle2">{item.planId.name} Plan</Typography>
                                <StatusBadge status={item.status} />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(item.startDate).toLocaleDateString()} -{" "}
                                {item.endDate ? new Date(item.endDate).toLocaleDateString() : "Present"}
                              </Typography>
                              {item.cancelReason && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Cancellation reason: {item.cancelReason}
                                </Typography>
                              )}
                            </Box>
                          ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Credit Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {credits ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: "100%" }}>
                          <Typography variant="h6" gutterBottom>
                            Credit Balance
                          </Typography>
                          <Divider sx={{ mb: 2 }} />

                          <Typography variant="h3" gutterBottom>
                            {credits.availableCredits}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Available credits
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Total Credits: {credits.totalCredits}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Used Credits: {credits.totalUsage}
                          </Typography>
                          <Button variant="contained" size="small" sx={{ mt: 2 }} onClick={handleManageCredits}>
                            Manage Credits
                          </Button>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: "100%" }}>
                          <Typography variant="h6" gutterBottom>
                            Recent Credit Activity
                          </Typography>
                          <Divider sx={{ mb: 2 }} />

                          <Box sx={{ textAlign: "center", py: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              View detailed credit history and manage credits
                            </Typography>
                            <Button 
                              variant="outlined" 
                              size="small" 
                              onClick={handleManageCredits}
                              sx={{ borderRadius: 2 }}
                            >
                              View Credit Details
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        No credit information available for this customer.
                      </Typography>
                      <Button variant="contained" onClick={handleManageCredits}>
                        Add Credits
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Contact Person
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {customer.contactPerson &&
                        (customer.contactPerson.name ||
                          customer.contactPerson.email ||
                          customer.contactPerson.phone) ? (
                          <>
                            {customer.contactPerson.name && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Name
                                </Typography>
                                <Typography variant="body1">{customer.contactPerson.name}</Typography>
                              </Box>
                            )}

                            {customer.contactPerson.email && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Email
                                </Typography>
                                <Typography variant="body1">{customer.contactPerson.email}</Typography>
                              </Box>
                            )}

                            {customer.contactPerson.phone && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Phone
                                </Typography>
                                <Typography variant="body1">{customer.contactPerson.phone}</Typography>
                              </Box>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No contact person information provided
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Address
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {customer.address &&
                        (customer.address.line1 || customer.address.city || customer.address.country) ? (
                          <>
                            {customer.address.line1 && (
                              <Typography variant="body1">{customer.address.line1}</Typography>
                            )}

                            {customer.address.line2 && (
                              <Typography variant="body1">{customer.address.line2}</Typography>
                            )}

                            {(customer.address.city || customer.address.state || customer.address.postalCode) && (
                              <Typography variant="body1">
                                {[customer.address.city, customer.address.state, customer.address.postalCode]
                                  .filter(Boolean)
                                  .join(", ")}
                              </Typography>
                            )}

                            {customer.address.country && (
                              <Typography variant="body1">{customer.address.country}</Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No address information provided
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </>
      ) : (
        <Box>
          <Button variant="outlined" onClick={handleCancelEdit} sx={{ mb: 3 }}>
            Cancel Editing
          </Button>
          <CustomerForm
            customer={customer}
            onSubmit={handleUpdate}
            loading={saving}
            isEdit={true}
            handleCancel={handleCancelEdit}
          />
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {customer.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerDetails

